import { useState } from 'react';
import type { HostAuditEntry } from '@teachpartner/contracts';
import { describeError } from '../api/client';
import { useServices } from '../app-context';
import { ErrorBanner } from '../components/badges';
import { useAsync } from '../hooks/use-async';

const PAGE = 50;

export function AuditPage() {
  const { api } = useServices();
  const [tenantCode, setTenantCode] = useState('');
  const [filter, setFilter] = useState('');
  const first = useAsync(
    () => api.audit({ tenantCode: filter || undefined, limit: PAGE }),
    [api, filter],
  );
  const [more, setMore] = useState<{ entries: HostAuditEntry[]; next: number | null } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const entries = [...(first.data?.entries ?? []), ...(more?.entries ?? [])];
  const next = more ? more.next : (first.data?.nextBefore ?? null);

  const loadMore = async () => {
    if (next === null) return;
    try {
      const page = await api.audit({ tenantCode: filter || undefined, before: next, limit: PAGE });
      setMore({ entries: [...(more?.entries ?? []), ...page.entries], next: page.nextBefore });
    } catch (e) {
      setError(describeError(e));
    }
  };

  return (
    <div>
      <h1>Audit log</h1>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setMore(null);
          setFilter(tenantCode.trim().toUpperCase());
        }}
      >
        <label>
          Tenant code
          <input value={tenantCode} onChange={(e) => setTenantCode(e.target.value)} />
        </label>
        <button type="submit">Filter</button>
      </form>
      <ErrorBanner message={error ?? first.error} />
      <table>
        <thead>
          <tr>
            <th>When</th>
            <th>Actor</th>
            <th>Action</th>
            <th>Tenant</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id}>
              <td>{e.at}</td>
              <td>{e.actor}</td>
              <td>{e.action}</td>
              <td>{e.tenantCode ?? '—'}</td>
              <td>
                <code>{JSON.stringify(e.details)}</code>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {next !== null && (
        <button type="button" onClick={() => void loadMore()}>
          Load more
        </button>
      )}
    </div>
  );
}
